import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiAdminCategory } from '../../core/api/api.types';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { FilterBarComponent } from '../../shared/components/filter-bar/filter-bar.component';
import {
  EMPTY_FILTER_VALUES,
  FilterBarConfig,
  FilterBarValues,
} from '../../shared/components/filter-bar/filter-bar.types';

const VERTICAL_LABELS: Record<string, string> = {
  auto:     'Auto',
  beauty:   'Beauty',
  cleaning: 'Cleaning',
  home:     'Home',
};

@Component({
  selector: 'op-service-categories-page',
  imports: [
    RouterModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FilterBarComponent,
  ],
  templateUrl: './service-categories.page.html',
  styleUrl: './service-categories.page.scss',
})
export class ServiceCategoriesPage implements OnInit {
  private readonly referenceApi = inject(ReferenceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly allCategories = signal<ApiAdminCategory[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  // ── Filter config ─────────────────────────────────────────────────────────
  readonly filterConfig: FilterBarConfig = {
    search: true,
    categories: true,
    cityAvailability: true,
    vertical: true,
    isActive: true,
  };

  readonly filterValues = signal<FilterBarValues>({ ...EMPTY_FILTER_VALUES });
  readonly filterInitialValues = signal<Partial<FilterBarValues>>({});

  // ── Filter option lists (derived from loaded data) ────────────────────────
  readonly categoryOptions = computed(() =>
    this.allCategories().map(c => ({ id: c.category_id, name: c.name, vertical: c.vertical })),
  );

  readonly cityOptions = computed(() => {
    const codes = new Set<string>();
    this.allCategories().forEach(c => c.city_availability.forEach(a => codes.add(a.city_code)));
    return [...codes].sort().map(code => ({ code, label: code }));
  });

  readonly verticalOptions = computed(() => {
    const verticals = [...new Set(this.allCategories().map(c => c.vertical))].sort();
    return verticals.map(v => ({ value: v, label: this.verticalLabel(v) }));
  });

  // ── Client-side filtered list ─────────────────────────────────────────────
  readonly categories = computed(() => {
    const f = this.filterValues();
    let result = this.allCategories();

    if (f.search.trim()) {
      const q = f.search.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q));
    }

    if (f.categoryIds.length) {
      result = result.filter(c => f.categoryIds.includes(c.category_id));
    }

    if (f.cityAvailability.length) {
      result = result.filter(c =>
        c.city_availability.some(a => f.cityAvailability.includes(a.city_code)),
      );
    }

    if (f.vertical) {
      result = result.filter(c => c.vertical === f.vertical);
    }

    if (f.isActive !== '') {
      const active = f.isActive === 'true';
      result = result.filter(c => c.is_active === active);
    }

    return result;
  });

  async ngOnInit(): Promise<void> {
    // Restore filters from URL before loading
    const q = this.route.snapshot.queryParams;
    const initial: FilterBarValues = {
      search:           q['search']           || '',
      categoryIds:      q['categoryIds']  ? q['categoryIds'].split(',').filter(Boolean)  : [],
      cityCodes:        [],
      cityAvailability: q['cityAvailability'] ? q['cityAvailability'].split(',').filter(Boolean) : [],
      vertical:         q['vertical']         || '',
      trustStatus:      '',
      insuranceVerified: '',
      isActive:         q['isActive']         || '',
    };
    this.filterInitialValues.set(initial);
    this.filterValues.set(initial);

    try {
      const res = await this.referenceApi.adminCategories();
      this.allCategories.set(res.items);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load service categories');
    } finally {
      this.loading.set(false);
    }
  }

  onFilterChange(values: FilterBarValues): void {
    this.filterValues.set(values);
    this.writeUrl(values);
  }

  private writeUrl(f: FilterBarValues): void {
    const queryParams: Record<string, string | null> = {
      search:           f.search                                 || null,
      categoryIds:      f.categoryIds.length      ? f.categoryIds.join(',')      : null,
      cityAvailability: f.cityAvailability.length ? f.cityAvailability.join(',') : null,
      vertical:         f.vertical                              || null,
      isActive:         f.isActive                              || null,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'replace',
      replaceUrl: true,
    });
  }

  verticalLabel(vertical: string): string {
    return VERTICAL_LABELS[vertical] ??
      vertical.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  activeCityCount(cat: ApiAdminCategory): number {
    return cat.city_availability.filter(c => c.is_active).length;
  }

  totalCityCount(cat: ApiAdminCategory): number {
    return cat.city_availability.length;
  }

  cityAvailabilitySummary(cat: ApiAdminCategory): string {
    const active = cat.city_availability.filter(c => c.is_active);
    if (!active.length) return '—';
    return active.map(c => c.city_code).join(', ');
  }

  inactiveCitySummary(cat: ApiAdminCategory): string {
    const inactive = cat.city_availability.filter(c => !c.is_active);
    if (!inactive.length) return '—';
    return inactive.map(c => c.city_code).join(', ');
  }

  inactiveCityCount(cat: ApiAdminCategory): number {
    return cat.city_availability.filter(c => !c.is_active).length;
  }

  exampleRequestsSummary(cat: ApiAdminCategory): string {
    const items = cat.example_requests;
    if (!items?.length) return '—';
    const preview = items.slice(0, 2).join('; ');
    return items.length > 2 ? `${preview} +${items.length - 2} more` : preview;
  }

  truncateDescription(text: string, max = 90): string {
    if (!text) return '—';
    return text.length > max ? text.slice(0, max).trimEnd() + '…' : text;
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }
}